import * as moment from 'moment';

export const DefaultDateConstants = {
  minDate: new Date(1900, 0, 1, 0, 0, 0, 0),
  maxDate: new Date(2100, 11, 31, 23, 59, 59, 999),
};

export interface IDateModel {
  day: string;
  month: string;
  year: string;
  formatted: string;
  momentObj: moment.Moment;
  isSet?: boolean;
}

export interface RangeType {
  type: FilterDateSelectionType;
  range: IDateRange;
}

export class DateModel implements IDateModel {
  day: string;
  month: string;
  year: string;
  formatted: string;
  momentObj: moment.Moment;
  isSet: boolean;

  constructor(obj?: IDateModel) {
    this.day = obj && obj.day ? obj.day : null;
    this.month = obj && obj.month ? obj.month : null;
    this.year = obj && obj.year ? obj.year : null;
    this.formatted = obj && obj.formatted ? obj.formatted : null;
    this.momentObj = obj && obj.momentObj ? obj.momentObj : null;
    this.isSet = obj && obj.isSet ? obj.isSet : false;
  }

  setDate(date: Date): void {
    if (date) {
      this.day = (date.getDate() >= 10 ? '' : '0') + date.getDate();
      this.month = (date.getMonth() >= 9 ? '' : '0') + (date.getMonth() + 1);
      this.year = date.getFullYear().toString();
      this.momentObj = moment(date);
      this.isSet = true;
      this.formatted = this.getFormatDate();
    }
  }

  getMoment(): moment.Moment {
    return this.momentObj;
  }

  getFormatDate(format: string = 'MMM Do, YYYY'): string {
    if (!format) {
      format = 'MMM Do, YYYY';
    }
    if (this.momentObj) {
      return this.momentObj.format(format);
    }
    return null;
  }

  getDate(): Date {
    return this.momentObj ? this.momentObj.toDate() : new Date();
  }
}

export interface IDateRange {
  dateFrom: Date;
  dateTo: Date;
}

export interface IDateModelRange {
  dateFrom: DateModel;
  dateTo: DateModel;
}

export interface CalendarDays {
  firstCalendar: CalendarDate[];
  secondCalendar: CalendarDate[];
}

export enum SelectedStates {
  NotSelected,
  Selected,
  BetweenSelected,
}

export interface CalendarInfo {
  display?: string;
  selected: boolean | SelectedStates;
}

export interface CalendarDate extends CalendarInfo {
  day: number;
  month: number;
  year: number;
  enabled: boolean;
  today: boolean;
  momentObj: moment.Moment;
  dayOfWeek?: string;
}

export interface MonthInfo extends CalendarInfo {
  monthNumber: number;
}

export enum DatePickerSelectionMode {
  HISTORIC_ONLY,
  FUTURE_DISABLED,
  PAST_DISABLED,
  ANY,
}

export interface IDatePickerOptions {
  minDate?: Date;
  maxDate?: Date;
  format?: string;
  defaultRangeSelections?: FilterDateSelectionType[];

  infoMessage?: string;
  infoFromDate?: Date;
  infoToDate?: Date;

  selectionMode?: DatePickerSelectionMode;

  //below is deprecated

  partialDate?: string;
  selectYearText?: string;

  hideTodayButton?: boolean;
  initialDate?: Date;
  endDate?: Date;

  autoApply?: boolean;
  style?: 'normal' | 'big' | 'bold';
  locale?: string;
  todayText?: string;
  clearText?: string;
  hideInput?: boolean;
  firstWeekdaySunday?: boolean;
}

export class DatePickerOptions implements IDatePickerOptions {
  minDate?: Date;
  maxDate?: Date;
  format?: string;
  defaultRangeSelections?: FilterDateSelectionType[];

  infoMessage?: string;
  infoFromDate?: Date;
  infoToDate?: Date;

  selectionMode?: DatePickerSelectionMode;

  partialDate?: string;
  selectYearText?: string;

  hideTodayButton = false;
  initialDate?: Date;
  endDate?: Date;

  autoApply?: boolean;
  style?: 'normal' | 'big' | 'bold';
  locale?: string;
  todayText?: string;
  clearText?: string;
  hideInput = false;
  firstWeekdaySunday?: boolean;

  constructor(obj?: IDatePickerOptions) {
    this.autoApply = !!(obj && obj.autoApply === true);
    this.style = obj && obj.style ? obj.style : 'normal';
    this.locale = obj && obj.locale ? obj.locale : 'en';

    this.initialDate = obj && obj.initialDate ? obj.initialDate : null;
    this.endDate = obj && obj.endDate ? obj.endDate : null;
    this.partialDate = obj && obj.partialDate ? obj.partialDate : null;
    this.firstWeekdaySunday = obj && obj.firstWeekdaySunday ? obj.firstWeekdaySunday : false;
    this.format = obj && obj.format ? obj.format : 'MMM Do, YYYY';
    this.selectYearText = obj && obj.selectYearText ? obj.selectYearText : 'Select Year';
    this.hideTodayButton = obj && obj.hideTodayButton;
    this.defaultRangeSelections = obj && obj.defaultRangeSelections ? obj.defaultRangeSelections : [];
    this.minDate = obj && obj.minDate ? obj.minDate : DefaultDateConstants.minDate;
    this.maxDate = obj && obj.maxDate ? obj.maxDate : DefaultDateConstants.maxDate;
    this.selectionMode = obj && obj.selectionMode ? obj.selectionMode : DatePickerSelectionMode.HISTORIC_ONLY;

    this.infoMessage = obj && obj.infoMessage ? obj.infoMessage : '';
    this.infoFromDate = obj && obj.infoFromDate;
    this.infoToDate = obj && obj.infoToDate;

    const today = moment().toDate();
    const yesterday = moment().subtract(1, 'days').toDate();
    switch (this.selectionMode) {
      case DatePickerSelectionMode.ANY:
        break;

      case DatePickerSelectionMode.PAST_DISABLED:
        this.minDate = this.minDate < today ? today : this.minDate;
        break;

      case DatePickerSelectionMode.FUTURE_DISABLED:
        if (this.maxDate >= today) {
          this.maxDate = today;
        }
        break;

      case DatePickerSelectionMode.HISTORIC_ONLY:
      default:
        if (this.maxDate >= today) {
          this.maxDate = yesterday;
        }
        break;
    }
  }
}

export const FilterDateSelectionTypeEnum: { [key: string]: FilterDateSelectionType } = {
  Yesterday: 'Yesterday',
  Today: 'Today',
  LastWeek: 'Last Week',
  Week: 'Week',
  LastMonth: 'Last Month',
  Month: 'Month',
  Last7Days: 'Last 7 Days',
  Last30Days: 'Last 30 Days',
  CustomRange: 'Custom',
  CustomDate: 'Custom Date',
};

export const DATE_SELECT_OPTIONS: DropDownOption[] = [
  { display: 'date_today', value: FilterDateSelectionTypeEnum.Today },
  { display: 'date_yesterday', value: FilterDateSelectionTypeEnum.Yesterday },
  { display: 'date_last_7_days', value: FilterDateSelectionTypeEnum.Last7Days, tooltip: 'date_last_7_days_tooltip' },
  { display: 'date_last_30_days', value: FilterDateSelectionTypeEnum.Last30Days, tooltip: 'date_last_30_days_tooltip' },
  {
    display: 'date_last_calendar_week',
    value: FilterDateSelectionTypeEnum.LastWeek,
    tooltip: 'date_last_calendar_week_tooltip',
  },
  { display: 'date_this_week', value: FilterDateSelectionTypeEnum.Week },
  { display: 'date_last_calendar_month', value: FilterDateSelectionTypeEnum.LastMonth },
  { display: 'date_this_month', value: FilterDateSelectionTypeEnum.Month },
  { display: 'date_custom_range', value: FilterDateSelectionTypeEnum.CustomRange },
  { display: 'date_custom_date', value: FilterDateSelectionTypeEnum.CustomDate },
];

export class DropDownOption {
  value: FilterDateSelectionType;
  display?: string;
  disabled?: boolean;
  tooltip?: string;
  constructor(value: FilterDateSelectionType, display: string, disabled?: boolean, tooltip?: string) {
    this.value = value;
    this.display = display;
    this.disabled = !!disabled;
    this.tooltip = tooltip;
  }
}

export type FilterDateSelectionType =
  | 'Yesterday'
  | 'Today'
  | 'Last Week'
  | 'Week'
  | 'Last Month'
  | 'Month'
  | 'Custom Date'
  | 'Custom'
  | 'Last 7 Days'
  | 'Last 30 Days';

export function isFilterDateCustom(type: FilterDateSelectionType): boolean {
  return type === 'Custom Date' || type === 'Custom';
}

export function isDateSelectionTypeSimilar(type1: FilterDateSelectionType, type2: FilterDateSelectionType): boolean {
  return type1 === type2 || (isFilterDateCustom(type1) && isFilterDateCustom(type2));
}

export function allowsCustomRange(selectionTypes: FilterDateSelectionType[]): boolean {
  return !!selectionTypes.find(type => type === FilterDateSelectionTypeEnum.CustomRange);
}
